"use client";

import { useState, useRef, useCallback, useEffect } from "react";
import {
  PAYMENT_POLL_INTERVAL_MS,
  PAYMENT_POLL_MAX_DURATION_MS,
} from "@/lib/constants/payment-polling";

// ── Types ────────────────────────────────────────────────────────

export type PollOutcome =
  | "idle"        // not yet started
  | "polling"     // actively polling
  | "success"     // payment confirmed
  | "underpaid"   // paid but less than expected
  | "failed"      // payment failed or cancelled (e.g. MoMo authorization error)
  | "timeout";    // max duration reached without confirmation

export interface PollResult {
  /** Current state of the poll */
  outcome: PollOutcome;
  /** Human-readable detail (e.g. underpaid message from API) */
  detail: string | null;
  /** True while any fetch is in-flight or interval is active */
  isChecking: boolean;
  /** Start (or restart) the poll loop */
  start: () => void;
  /** Manually stop polling */
  stop: () => void;
}

interface PollOptions {
  /** Transaction reference (tx_ref) to check */
  txRef: string;
  /** Called once payment is confirmed */
  onConfirmed?: () => void;
  /** Max poll duration in ms (default: PAYMENT_POLL_MAX_DURATION_MS) */
  maxMs?: number;
  /** Interval between polls in ms (default: PAYMENT_POLL_INTERVAL_MS) */
  intervalMs?: number;
}

// ── Success detection ────────────────────────────────────────────

/**
 * Returns true when the API response indicates the user's billing is active.
 *
 * The payment-status endpoint may return:
 *   { status: "success" }            ← active-verification activated it
 *   { status: "active" }             ← Firestore already marked active
 *   { subscriptionStatus: "active" } ← alternative envelope
 *   { billingDTO: { subscriptionStatus: "active" } }
 *   { data: { subscriptionStatus: "active" } }
 */
function isConfirmed(res: Record<string, any> | null | undefined): boolean {
  if (!res) return false;
  if (res.status === "success" || res.status === "active") return true;
  if (res.subscriptionStatus === "active") return true;
  if (res.billingDTO?.subscriptionStatus === "active") return true;
  if (res.data?.subscriptionStatus === "active") return true;
  return false;
}

function isUnderpaid(res: Record<string, any> | null | undefined): boolean {
  if (!res) return false;
  return res.status === "underpaid";
}

/**
 * Returns true when the API response indicates terminal failure.
 *
 * The payment-status endpoint returns { status: "failed" } when
 * the Flutterwave transaction is failed or cancelled (e.g.
 * TARGET_AUTHORIZATION_ERROR on MoMo Ghana).
 */
function isFailed(res: Record<string, any> | null | undefined): boolean {
  if (!res) return false;
  if (res.status === "failed") return true;
  const dataStatus = res.data?.status;
  if (dataStatus === "failed" || dataStatus === "cancelled") return true;
  return false;
}

// ── Hook ─────────────────────────────────────────────────────────

export function useFlutterwavePaymentPoll({
  txRef,
  onConfirmed,
  maxMs = PAYMENT_POLL_MAX_DURATION_MS,
  intervalMs = PAYMENT_POLL_INTERVAL_MS,
}: PollOptions): PollResult {
  const [outcome, setOutcome] = useState<PollOutcome>("idle");
  const [detail, setDetail] = useState<string | null>(null);

  // refs survive re-renders without triggering them
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const mountedRef = useRef(true);
  const onConfirmedRef = useRef(onConfirmed);
  onConfirmedRef.current = onConfirmed;

  // ── cleanup helper ──
  const clearPoll = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // ── single poll tick ──
  const tick = useCallback(async () => {
    // Timeout guard
    if (
      startTimeRef.current &&
      Date.now() - startTimeRef.current >= maxMs
    ) {
      clearPoll();
      if (mountedRef.current) {
        setOutcome("timeout");
        setDetail(
          "Still confirming. Please wait a few minutes and try again, or contact support."
        );
      }
      return;
    }

    try {
      const res = await fetch(
        `/api/flutterwave/payment-status?tx_ref=${encodeURIComponent(txRef)}`
      );
      const data = await res.json();
      if (!mountedRef.current) return;

      if (isConfirmed(data)) {
        clearPoll();
        setOutcome("success");
        setDetail(null);
        onConfirmedRef.current?.();
        return;
      }

      if (isUnderpaid(data)) {
        clearPoll();
        setOutcome("underpaid");
        setDetail(data.detail ?? "Payment amount is less than expected.");
        return;
      }

      if (isFailed(data)) {
        clearPoll();
        setOutcome("failed");
        setDetail(
          data.reason ??
          data.detail ??
          "Payment wasn't completed (authorization failed). Please try again."
        );
        return;
      }

      // Otherwise keep polling (status is "pending" / "inactive" / etc.)
    } catch (err) {
      console.error("[useFlutterwavePaymentPoll] fetch error:", err);
      // Don't stop polling on transient network errors
    }
  }, [txRef, maxMs, clearPoll]);

  // ── start / stop ──
  const start = useCallback(() => {
    // Prevent overlapping polls
    clearPoll();

    setOutcome("polling");
    setDetail(null);
    startTimeRef.current = Date.now();

    // Immediate first tick
    tick();

    // Subsequent ticks
    intervalRef.current = setInterval(tick, intervalMs);
  }, [tick, intervalMs, clearPoll]);

  const stop = useCallback(() => {
    clearPoll();
    if (mountedRef.current) {
      setOutcome("idle");
      setDetail(null);
    }
  }, [clearPoll]);

  // ── unmount cleanup ──
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearPoll();
    };
  }, [clearPoll]);

  return {
    outcome,
    detail,
    isChecking: outcome === "polling",
    start,
    stop,
  };
}
